import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { useTranslation } from "react-i18next";

import { trackPageView } from "../services/googleServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBars, 
  faCaretDown, 
  faMobileAlt, 
  faHandshake, 
  faStar, 
  faSignOutAlt 
} from "@fortawesome/free-solid-svg-icons";
import defaultAvatar from "../img_attributions/profile-icon-png-896.png";
import LanguageSwitcher from "./shared/LanguageSwitcher";

const Navbar = () => {
  const location = useLocation();
  const { authState, oktaAuth } = useOktaAuth();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  const logout = useCallback(() => oktaAuth.signOut(), [oktaAuth]);

  const toggleMobileMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
    setIsMenuOpen(false);
  }, [location]);

  // Handle outside click and Escape key to close mobile dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  return (
    <nav className="navbar navbar-expand-lg fixed-top navbar-dark bg-warning shadow-sm">
      <div className="container-fluid">
        <div className="d-flex align-items-center">
          <Link to="/" className="navbar-brand me-1">
            <img src={(process.env.PUBLIC_URL || '') + "/favicon-32x32.png?v=0.1.31"} title="https://www.healthdash.lv" alt="HealthDash Logo" className="d-inline-block align-top" />
          </Link>
          <LanguageSwitcher />
        </div>

        {/* Mobile Dropdown for smaller screens / phones: styled identically to "Install Android App" pill */}
        <div className="d-lg-none ms-auto position-relative" ref={mobileMenuRef}>
          <button
            type="button"
            className="nav-link text-white bg-dark rounded-pill px-3 py-1 font-weight-bold shadow-sm d-flex align-items-center border-0"
            style={{ fontSize: "0.85rem", cursor: "pointer" }}
            onClick={toggleMobileMenu}
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
            aria-label={t('nav.menu', 'Menu')}
            data-testid="mobile-menu-toggle"
          >
            <FontAwesomeIcon icon={faBars} className="me-1" />
            <span>{t('nav.menu', 'Menu')}</span>
            <FontAwesomeIcon icon={faCaretDown} className="ms-1" style={{ fontSize: "0.75rem" }} />
          </button>

          {isMenuOpen && (
            <div
              className="mobile-nav-menu shadow-lg"
              role="menu"
              data-testid="mobile-nav-menu"
            >
              <a
                className="mobile-nav-item mobile-nav-install-app fw-bold text-decoration-none"
                href="https://play.google.com/store/apps/details?id=com.linardsl.libhacontimig.hm.final"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                role="menuitem"
              >
                <FontAwesomeIcon icon={faMobileAlt} className="me-2 text-warning" />
                {t('nav.installApp', 'Install Android App')}
              </a>
              <div className="mobile-nav-divider" />
              <NavLink className="mobile-nav-item text-decoration-none" to="/partners" onClick={closeMobileMenu} role="menuitem">
                <FontAwesomeIcon icon={faHandshake} className="me-2 text-warning" />
                {t('nav.partners', 'Partners')}
              </NavLink>
              <NavLink className="mobile-nav-item text-decoration-none" to="/" end onClick={closeMobileMenu} role="menuitem">
                {t('nav.home', 'Home')}
              </NavLink>
              <NavLink className="mobile-nav-item text-decoration-none" to="/favourite" onClick={closeMobileMenu} role="menuitem">
                {t('nav.favourite', 'Favourite')}
              </NavLink>
              <div className="mobile-nav-divider" />
              <NavLink className="mobile-nav-item text-decoration-none" to="/user" onClick={closeMobileMenu} role="menuitem">
                {authState?.isAuthenticated && <FontAwesomeIcon icon={faStar} className="me-2 text-warning" />}
                {t('nav.dashboard', 'Dashboard')}
              </NavLink>
              {authState?.isAuthenticated && (
                <>
                  <div className="mobile-nav-divider" />
                  <NavLink className="mobile-nav-item text-decoration-none" to="/profile" onClick={closeMobileMenu} role="menuitem">
                    <img 
                      src={defaultAvatar} 
                      alt="" 
                      className="rounded-circle me-2 border border-secondary" 
                      style={{ width: '18px', height: '18px', objectFit: 'cover' }} 
                    />
                    {t('nav.profile', 'Profile')}
                  </NavLink>
                  <button
                    type="button"
                    className="mobile-nav-item text-start border-0 bg-transparent text-danger fw-bold w-100 d-flex align-items-center"
                    onClick={() => {
                      closeMobileMenu();
                      logout();
                    }}
                    role="menuitem"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    {t('nav.logout', 'Logout')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Desktop Navbar for screens that fit all header elements */}
        <div className="collapse navbar-collapse d-none d-lg-flex" id="navbarsExample02">
          <ul className="navbar-nav ms-auto align-items-center">
            <li className="nav-item me-3">
              <a 
                className="nav-link text-white bg-dark rounded-pill px-3 py-1 font-weight-bold shadow-sm" 
                href="https://play.google.com/store/apps/details?id=com.linardsl.libhacontimig.hm.final" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ fontSize: "0.85rem" }}
              >
                <FontAwesomeIcon icon={faMobileAlt} className="me-1" />
                {t('nav.installApp', 'Install Android App')}
              </a>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/partners">
                <FontAwesomeIcon icon={faHandshake} className="me-1" />
                {t('nav.partners', 'Partners')}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/" end>{t('nav.home', 'Home')}</NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/favourite">{t('nav.favourite', 'Favourite')}</NavLink>
            </li>
            <li className="nav-item nav-separator d-flex align-items-center text-white-50 px-2" aria-hidden="true">
              <span className="nav-divider">|</span>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/user">
                {authState?.isAuthenticated && <FontAwesomeIcon icon={faStar} className="me-1 text-warning" />}
                {t('nav.dashboard', 'Dashboard')}
              </NavLink>
            </li>
            {authState?.isAuthenticated && (
              <>
                <li className="nav-item">
                  <NavLink className="nav-link d-flex align-items-center" to="/profile">
                    <img 
                      src={defaultAvatar} 
                      alt="" 
                      className="rounded-circle me-1 border border-light" 
                      style={{ width: '18px', height: '18px', objectFit: 'cover' }} 
                    />
                    {t('nav.profile', 'Profile')}
                  </NavLink>
                </li>
                <li className="nav-item">
                  <button className="btn btn-link nav-link text-white d-flex align-items-center" onClick={logout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
                    {t('nav.logout', 'Logout')}
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
